/**
 * User + workspace settings contracts — RO-2-09d / #44417.
 *
 * Ported from `excellent/src/projects/user-settings-actions.ts` (12 `"use
 * server"` actions). No `route.ts` twin exists under Next; the settings
 * pages still call the server actions. Live Next call sites are deliberately
 * NOT repointed (`ccb89192`) — repointing belongs with the client cutover.
 *
 * Inventory (`docs/rollover/inventory/actions.md` lines 374–385):
 *
 *   setTheme                      → POST /api/settings/appearance/theme
 *   setDensity                    → POST /api/settings/appearance/density
 *   setAccent                     → POST /api/settings/appearance/accent
 *   saveAppearanceForm            → POST /api/settings/appearance
 *   saveProfileForm               → POST /api/settings/user/profile
 *   setStartPage                  → POST /api/settings/user/start-page
 *   setTimeFormat                 → POST /api/settings/user/time-format
 *   setWeekStart                  → POST /api/settings/user/week-start
 *   setReduceMotion               → POST /api/settings/user/reduce-motion
 *   setNotificationPreference     → POST /api/settings/user/notifications
 *   saveWorkspaceIdentityForm     → POST /api/settings/workspace/identity
 *   saveRetentionPolicyForm       → POST /api/settings/workspace/retention
 *
 * FormData-typed signatures are reshaped to JSON bodies with the same field
 * names the forms already submit. Invalid enum values are no-ops on the
 * server (matching the source actions' silent early return), so the contract
 * accepts the string the form sends and the handler filters.
 *
 * Dropped `revalidatePath` call sites (L5 invalidation graph handoff):
 *   appearance.*          → /settings/appearance
 *   profile + prefs + notif → /settings/user
 *   workspace identity    → /settings/workspace/identity
 *   retention             → /settings/workspace, /database
 */

import { z } from "zod";
import type { Operation } from "../types";

const okResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof okResponse>;

// ── Appearance ──────────────────────────────────────────────────────────────

const themeBody = z.object({ value: z.string() });
export type ThemeBody = z.infer<typeof themeBody>;

const densityBody = z.object({ value: z.string() });
export type DensityBody = z.infer<typeof densityBody>;

const accentBody = z.object({ value: z.string() });
export type AccentBody = z.infer<typeof accentBody>;

const appearanceFormBody = z.object({
  theme: z.string().optional(),
  density: z.string().optional(),
  accent: z.string().optional(),
});
export type AppearanceFormBody = z.infer<typeof appearanceFormBody>;

const setThemeOp: Operation<void, void, ThemeBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/appearance/theme",
  body: themeBody,
  response: okResponse,
};

const setDensityOp: Operation<void, void, DensityBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/appearance/density",
  body: densityBody,
  response: okResponse,
};

const setAccentOp: Operation<void, void, AccentBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/appearance/accent",
  body: accentBody,
  response: okResponse,
};

const saveAppearanceFormOp: Operation<void, void, AppearanceFormBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/appearance",
  body: appearanceFormBody,
  response: okResponse,
};

// ── Profile + preferences + notifications ───────────────────────────────────

const profileBody = z.object({
  display_name: z.string(),
});
export type ProfileBody = z.infer<typeof profileBody>;

const startPageBody = z.object({ value: z.string() });
export type StartPageBody = z.infer<typeof startPageBody>;

const timeFormatBody = z.object({ value: z.string() });
export type TimeFormatBody = z.infer<typeof timeFormatBody>;

const weekStartBody = z.object({ value: z.string() });
export type WeekStartBody = z.infer<typeof weekStartBody>;

const reduceMotionBody = z.object({ value: z.boolean() });
export type ReduceMotionBody = z.infer<typeof reduceMotionBody>;

const notificationBody = z.object({
  category: z.string(),
  channel: z.string(),
  enabled: z.boolean(),
});
export type NotificationBody = z.infer<typeof notificationBody>;

const saveProfileFormOp: Operation<void, void, ProfileBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/user/profile",
  body: profileBody,
  response: okResponse,
};

const setStartPageOp: Operation<void, void, StartPageBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/user/start-page",
  body: startPageBody,
  response: okResponse,
};

const setTimeFormatOp: Operation<void, void, TimeFormatBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/user/time-format",
  body: timeFormatBody,
  response: okResponse,
};

const setWeekStartOp: Operation<void, void, WeekStartBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/user/week-start",
  body: weekStartBody,
  response: okResponse,
};

const setReduceMotionOp: Operation<void, void, ReduceMotionBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/user/reduce-motion",
  body: reduceMotionBody,
  response: okResponse,
};

const setNotificationPreferenceOp: Operation<void, void, NotificationBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/user/notifications",
  body: notificationBody,
  response: okResponse,
};

// ── Workspace identity + retention ──────────────────────────────────────────

const workspaceIdentityBody = z.object({
  workspace_name: z.string(),
});
export type WorkspaceIdentityBody = z.infer<typeof workspaceIdentityBody>;

/**
 * Identity can fail typed (auth/membership/name) — same reasons as
 * `renameActiveWorkspace`. Success is `{ok:true}`; failure is in-band so a
 * form can surface the message without a 500.
 */
export type WorkspaceIdentityResult =
  | { ok: true }
  | {
      ok: false;
      reason: "unauthenticated" | "not-a-member" | "invalid-name" | "no-workspace";
      error: string;
    };

const workspaceIdentityResponse = z.custom<WorkspaceIdentityResult>();

const saveWorkspaceIdentityFormOp: Operation<
  void,
  void,
  WorkspaceIdentityBody,
  WorkspaceIdentityResult
> = {
  method: "POST",
  path: () => "/api/settings/workspace/identity",
  body: workspaceIdentityBody,
  response: workspaceIdentityResponse,
};

const retentionBody = z.object({
  /** Empty string clears the lever (fail-closed — setting deleted). */
  retention_event_days: z.string().optional(),
  /** Empty string clears; "0" is valid (keep only latest). */
  retention_raw_keep: z.string().optional(),
});
export type RetentionBody = z.infer<typeof retentionBody>;

const saveRetentionPolicyFormOp: Operation<void, void, RetentionBody, OkResponse> = {
  method: "POST",
  path: () => "/api/settings/workspace/retention",
  body: retentionBody,
  response: okResponse,
};

// ── Page READs (RO-3-09 / #44302) ───────────────────────────────────────────
//
// The Next settings pages were RSC server components that called core in-process
// (`getAppearance`, `getCurrentUserActor`, `getUserPreferences`,
// `getNotificationMatrix`, `getWorkspace`). A client route cannot; these five
// GETs are the transport for that page data. No Next `route.ts` twin exists —
// live Next pages stay on server actions / RSC (D6). Every handler was traced
// OUT transitively: pure reads only (profile does NOT lazy-create the human
// actor — that write stays on the first profile save).

const appearanceValues = z.object({
  theme: z.string(),
  density: z.string(),
  accent: z.string(),
});
export type AppearanceValuesDto = z.infer<typeof appearanceValues>;

const getAppearanceOp: Operation<void, void, void, AppearanceValuesDto> = {
  method: "GET",
  path: () => "/api/settings/appearance",
  response: appearanceValues,
};

const profileValues = z.object({
  id: z.number().nullable(),
  display_name: z.string().nullable(),
  external_id: z.string(),
  kind: z.string(),
});
export type ProfileValuesDto = z.infer<typeof profileValues>;

const getProfileOp: Operation<void, void, void, ProfileValuesDto> = {
  method: "GET",
  path: () => "/api/settings/user/profile",
  response: profileValues,
};

const preferencesValues = z.object({
  startPage: z.string(),
  timeFormat: z.string(),
  weekStart: z.string(),
  reduceMotion: z.boolean(),
});
export type PreferencesValuesDto = z.infer<typeof preferencesValues>;

const getPreferencesOp: Operation<void, void, void, PreferencesValuesDto> = {
  method: "GET",
  path: () => "/api/settings/user/preferences",
  response: preferencesValues,
};

/** Category × channel matrix — keys mirror NOTIFICATION_CATEGORIES/CHANNELS. */
const notificationsValues = z.record(z.record(z.boolean()));
export type NotificationsValuesDto = z.infer<typeof notificationsValues>;

const getNotificationsOp: Operation<void, void, void, NotificationsValuesDto> = {
  method: "GET",
  path: () => "/api/settings/user/notifications",
  response: notificationsValues,
};

const workspaceValues = z.object({
  id: z.number().nullable(),
  name: z.string(),
  slug: z.string().nullable(),
});
export type WorkspaceValuesDto = z.infer<typeof workspaceValues>;

const getWorkspaceOp: Operation<void, void, void, WorkspaceValuesDto> = {
  method: "GET",
  path: () => "/api/settings/workspace",
  response: workspaceValues,
};

// ── Barrel ──────────────────────────────────────────────────────────────────

export const userSettings = {
  setTheme: setThemeOp,
  setDensity: setDensityOp,
  setAccent: setAccentOp,
  saveAppearanceForm: saveAppearanceFormOp,
  saveProfileForm: saveProfileFormOp,
  setStartPage: setStartPageOp,
  setTimeFormat: setTimeFormatOp,
  setWeekStart: setWeekStartOp,
  setReduceMotion: setReduceMotionOp,
  setNotificationPreference: setNotificationPreferenceOp,
  saveWorkspaceIdentityForm: saveWorkspaceIdentityFormOp,
  saveRetentionPolicyForm: saveRetentionPolicyFormOp,
  // RO-3-09 page READs
  getAppearance: getAppearanceOp,
  getProfile: getProfileOp,
  getPreferences: getPreferencesOp,
  getNotifications: getNotificationsOp,
  getWorkspace: getWorkspaceOp,
};
